import { useEffect, useState, type FormEvent } from "react";
import { shortAddr } from "../../lib/api";
import {
  baseAddressFromAccountId,
  type X402Profile,
  type X402WalletLinkPreparation,
} from "../../lib/x402Profile";

interface Props {
  profile: X402Profile;
  memberAddress: string;
  preparation: X402WalletLinkPreparation | null;
  agentCashCommand: string | null;
  busy: boolean;
  error: string | null;
  onMemberAddressChange: (value: string) => void;
  onPrepare: () => void;
  onRefresh: () => void;
  onRevoke: (accountId: string) => void;
}

export function X402WalletLinkPanel(props: Props) {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const [copyFailure, setCopyFailure] = useState<{ command: string; message: string } | null>(null);
  const [revokeAccount, setRevokeAccount] = useState<string | null>(null);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1_000));

  useEffect(() => {
    if (!props.preparation) return;
    setNow(Math.floor(Date.now() / 1_000));
    const timer = window.setInterval(() => setNow(Math.floor(Date.now() / 1_000)), 1_000);
    return () => window.clearInterval(timer);
  }, [props.preparation]);

  const secondsRemaining = props.preparation ? Math.max(0, props.preparation.link.expiresAt - now) : 0;
  const expired = Boolean(props.preparation && secondsRemaining === 0);

  function submit(event: FormEvent) {
    event.preventDefault();
    props.onPrepare();
  }

  async function copyCommand() {
    if (!props.agentCashCommand) return;
    setCopyFailure(null);
    try {
      await navigator.clipboard.writeText(props.agentCashCommand);
      setCopiedCommand(props.agentCashCommand);
    } catch {
      setCopiedCommand(null);
      setCopyFailure({
        command: props.agentCashCommand,
        message: "Could not copy the command. Select the text and copy it manually.",
      });
    }
  }

  return (
    <section className="border-y border-faint py-8" aria-labelledby="linked-wallets-heading">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Step 3 · Wallet membership</p>
          <h2 id="linked-wallets-heading" className="mt-2 text-3xl font-display tracking-tight">Linked wallets</h2>
          <p className="mt-3 text-sm text-muted leading-relaxed">
            Each wallet signs its own link. The controller can revoke membership but can never spend from a member wallet.
          </p>
          {props.profile.discoveryMode === "private" && (
            <p className="mt-3 text-sm text-warning leading-relaxed">
              Publish the controller-only profile first. Wallet linking is available after discovery is on so the member signs and accepts the public policy itself.
            </p>
          )}
        </div>
        <span className="pill pill-faint btn-mono text-xs self-start md:self-auto">
          {props.profile.wallets.length} active {props.profile.wallets.length === 1 ? "wallet" : "wallets"}
        </span>
      </div>

      <ul className="mt-6 divide-y divide-faint border-y border-faint" aria-label="Active profile wallets">
        {props.profile.wallets.map((wallet) => {
          const address = baseAddressFromAccountId(wallet.accountId);
          const isController = wallet.isController || props.profile.controllerAccount?.toLowerCase() === wallet.accountId.toLowerCase();
          return (
            <li key={wallet.accountId} className="py-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="btn-mono text-sm break-all">{wallet.accountId}</p>
                <p className="mt-1 text-xs text-muted">
                  {isController ? "Controller" : "Member"} · {wallet.discoveryConsent === "public_all" ? "public" : "private"} · linked {formatDate(wallet.linkedAt)}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <span className="pill pill-olive btn-mono text-[10px]">
                  {wallet.proofKind.toUpperCase()} proof {address ? `· ${shortAddr(address)}` : ""}
                </span>
                {!isController && revokeAccount !== wallet.accountId && (
                  <button className="btn-mono text-xs text-muted hover:text-danger" type="button" onClick={() => setRevokeAccount(wallet.accountId)}>
                    Revoke link
                  </button>
                )}
              </div>
              {!isController && revokeAccount === wallet.accountId && (
                <div className="sm:basis-full border border-warning/40 bg-amber-soft rounded-sm p-3">
                  <p className="text-xs leading-relaxed">End this active relationship? This stops future profile aggregation but does not erase previously public copies.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button className="btn btn-ghost" type="button" disabled={props.busy} onClick={() => {
                      setRevokeAccount(null);
                      props.onRevoke(wallet.accountId);
                    }}>Confirm revocation</button>
                    <button className="btn btn-ghost" type="button" onClick={() => setRevokeAccount(null)}>Keep linked</button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <form className="mt-8 max-w-3xl" onSubmit={submit}>
        <label htmlFor="x402-member-wallet" className="block text-sm font-medium">Link another Base wallet</label>
        <p id="x402-member-wallet-help" className="mt-1 text-xs text-muted leading-relaxed">
          Enter its address to prepare a five-minute intent. That wallet must confirm the same link through SIWX.
        </p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <input
            id="x402-member-wallet"
            aria-describedby="x402-member-wallet-help"
            className="min-h-11 w-full border border-faint rounded-md bg-paper px-3 py-2 btn-mono text-sm"
            value={props.memberAddress}
            onChange={(event) => props.onMemberAddressChange(event.target.value)}
            placeholder="0x..."
            autoComplete="off"
            spellCheck={false}
            disabled={props.profile.discoveryMode === "private"}
          />
          <button className="btn btn-primary shrink-0" type="submit" disabled={props.profile.discoveryMode === "private" || props.busy || !props.memberAddress.trim()}>
            {props.busy ? "Preparing…" : "1. Prepare link"}
          </button>
        </div>
      </form>

      {props.preparation && props.agentCashCommand && (
        <section className="mt-6 border border-faint rounded-md bg-paper-deep p-5" aria-labelledby="agentcash-confirm-heading">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-olive-deep">Member proof required</p>
            <span className={`btn-mono text-xs ${expired ? "text-danger" : "text-muted"}`} aria-hidden="true">
              {expired ? "Link expired" : `Expires in ${formatDuration(secondsRemaining)}`}
            </span>
            <span className="sr-only" role="status" aria-live="polite">{expired ? "Link expired. Prepare a new link." : ""}</span>
          </div>
          <h3 id="agentcash-confirm-heading" className="mt-2 text-2xl font-display">2. Confirm from the member wallet</h3>
          <p className="mt-2 text-sm text-muted leading-relaxed">
            Run this where AgentCash holds the member key. The identity-only request moves no USDC and accepts only this profile link.
          </p>
          <div className="mt-4 border border-faint rounded-sm bg-paper p-4">
            <code className="block text-xs leading-relaxed whitespace-pre-wrap break-all">{props.agentCashCommand}</code>
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            {expired ? (
              <button className="btn btn-ghost" type="button" onClick={props.onPrepare} disabled={props.busy}>
                Prepare new link
              </button>
            ) : (
              <button className="btn btn-ghost" type="button" onClick={() => void copyCommand()}>
                {copiedCommand === props.agentCashCommand ? "Copied" : "Copy AgentCash command"}
              </button>
            )}
            <button className="btn btn-ghost" type="button" onClick={props.onRefresh} disabled={props.busy || expired}>
              {props.busy ? "Checking…" : "3. Check confirmation"}
            </button>
          </div>
          {copyFailure?.command === props.agentCashCommand && <p className="mt-3 text-sm text-danger" role="alert">{copyFailure.message}</p>}
          <dl className="mt-5 grid gap-4 sm:grid-cols-2">
            <Fact label="Member account" value={props.preparation.link.memberAccount} />
            <Fact label="Intent digest" value={props.preparation.link.intentDigest} />
          </dl>
        </section>
      )}

      {props.error && <p className="mt-4 text-sm text-danger" role="alert">{props.error}</p>}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0"><dt className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">{label}</dt><dd className="mt-1 btn-mono text-xs break-all">{value}</dd></div>;
}

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1_000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
